<template>
    <div class="section" id="section-research">
        <div class="section-head" id="sec-research"><span>Research and Publication</span></div>

        <a-tabs v-model:activeKey="currentTab">
            <a-tab-pane v-for="tag in tags" :key="tag" :tab="tag">
                <div v-if="tag == 'AI4VIS'">
                    <div class="research-head">Brief Statement</div>
                    I work on developing AI approaches for visualizations with the overarching goals
                    to improve user experience and democratize data visualizations. I propose
                    machine learning methods for automating responsive visualization design
                    <span class="work-short">[<PaperLink paperid="VIS20M" />]</span>, optimizing
                    aesthetics <span class="work-short">[<PaperLink paperid="CHI20" />]</span>, and
                    recommending dashboards for data analysis
                    <span class="work-short"
                        >[<PaperLink paperid="VIS21" />,<PaperLink paperid="VIS22b" />,
                        <PaperLink paperid="TVCG23" />]</span
                    >. I argue in a recent survey
                    <span class="work-short">[<PaperLink paperid="TVCG21" />]</span> that
                    "visualizations" are becoming a new data format that differs from traditional
                    data (e.g., image and text), thus posing distinct needs and challenges on
                    developing AI approaches for analyzing and manipulating visualizations. Towards
                    this vision, I have contributed methods for retrieving, processing and analyzing
                    visualization data
                    <span class="work-short">[<PaperLink paperid="CHI22" />]</span>.
                </div>
                <div v-else-if="tag == 'Smart Campus / Vis for Masses'">
                    <div class="research-head">Brief Statement</div>
                    I contribute visualization systems for collecting, analyzing, and visualizing
                    campus big data to facilitate data-driven decision-makings such as smart
                    transportation
                    <span class="work-short">[<PaperLink paperid="VisComm18" />]</span>. I
                    investigate how to design effective and understandable data visualizations for
                    the public
                    <span class="work-short"
                        >[<PaperLink paperid="VIS20" />,<PaperLink paperid="TVCG21" />]</span
                    >. In addition to understandbility, I also investigate misinformative
                    visualization and discuss potential solutions
                    <span class="work-short">[<PaperLink paperid="CGF22" />]</span>. More recently,
                    I study how to create cinematic data videos
                    <span class="work-short"
                        >[<PaperLink paperid="PVIS23" />,<PaperLink paperid="CHI23" />]</span
                    >.
                </div>
                <div v-else-if="tag == 'Multimodal Analysis and Visualization'">
                    <div class="research-head">Brief Statement</div>
                    I contribute visual analytical systems for analyzing presentation styles in
                    videos
                    <span class="work-short"
                        >[<PaperLink paperid="TVCG18" />, <PaperLink paperid="VIS19" />]</span
                    >, coaching voice modulation skills
                    <span class="work-short">[<PaperLink paperid="CHI19" />]</span>, and analyzing
                    gestures <span class="work-short">[<PaperLink paperid="TVCG22G" />]</span>.
                </div>
                <div v-else-if="tag == 'Visual Analytics'">
                    <div class="research-head">Brief Statement</div>
                    My goal is to strengthen the theoretical and practical underpinnings of VA
                    research. To that end, I conduct two interview studies to gather the critics and
                    replies about VA systems and discuss eight critical problems
                    <span class="work-short">[<PaperLink paperid="VIS22a" />]</span>. In a viewpoint with leading VA researchers <span class="work-short">[<PaperLink paperid="CGA23Viewpoint" />]</span>, we outline a research and development agenda for making VA application research more rigorous and impactful.
                </div>
                <div v-else-if="tag == 'All'">
                    <div class="research-head">Brief Statement</div>
                    My long-term research goal is to promote the democratization of visualization
                    and visual analytics, whereby creating applications in important domains such as
                    education, data science, media, and communication. To expand the user base and
                    open new application scenarios, it is necessary to propose faithful, novel,
                    attractive, professional, and useful visualization techniques.
                </div>

                <div v-for="publication in filteredPub" :key="publication.title">
                    <SinglePaper :publication="publication" />
                </div>
            </a-tab-pane>
        </a-tabs>
    </div>

    <!-- <div>
    <div v-for="publication in publications" :key="publication.title">
      <SinglePaper :publication="publication" />
    </div>
  </div> -->
</template>

<script lang="ts">
import "bootstrap/dist/css/bootstrap.css";
import * as _ from "underscore";

import { CommentOutlined } from "@ant-design/icons-vue";

import SinglePaper from "./SinglePaper.vue";
import PaperLink from "./components/PaperLink.vue";

import { defineComponent } from "vue";
import { useStore } from "vuex";

import { onMounted, computed, reactive, toRefs, h, watch } from "vue";
import { groupBy } from "underscore";

const publications = [
    {
        imgurl: "vis23attentionviz.png",
        title: "AttentionViz: A Global View of Transformer Attention",
        author: "Catherine Yeh, Yida Chen, Aoyu Wu, Cynthia Chen, Fernanda Viégas, and Martin Wattenberg",
        bookShort: "VIS/TVCG",
        book: "IEEE Transactions on Visualization and Computer Graphics (Proc. of VIS)",
        type: "Journal",
        year: "2023",
        badge: "badge-j",
        github: "",
        pdf: "https://arxiv.org/abs/2305.03210",
        otherinfo: "",
        tag: "Visual Analytics",
        about: "We create an interactive visualization tool, AttentionViz, based on these joint query-key embeddings, and use it to study attention mechanisms in both language and vision transformers.",
        id: "VIS23AttnViz",
    },
    {
        imgurl: "vis23InkSight.png",
        title: "InkSight: Leveraging Sketch Interaction for Documenting Chart Findings in Computational Notebooks",
        author: "Yanna Lin, Haotian Li, Leni Yang, Aoyu Wu, and Huamin Qu",
        bookShort: "VIS/TVCG",
        book: "IEEE Transactions on Visualization and Computer Graphics (Proc. of VIS)",
        type: "Journal",
        year: "2023",
        badge: "badge-j",
        github: "",
        pdf: "https://arxiv.org/abs/2307.07922",
        otherinfo: "",
        tag: "AI4VIS",
        about: "We propose a mixed-initiative computational notebook plugin that generates finding documentation based on the user's intent.",
        id: "VIS23InkSight",
    },
    {
        imgurl: "cga23viewpoint.png",
        title: "Grand Challenges in Visual Analytics Applications",
        author: "Aoyu Wu, Dazhen Deng, Min Chen, Shixia Liu, Daniel Keim, Ross Maciejewski, Silvia Miksch, Hendrik Strobelt, Fernanda Viegas, and Martin Wattenberg",
        bookShort: "CGA",
        book: "IEEE Computer Graphics and Applications [Visualization Viewpoint]",
        type: "Viewpoint",
        year: "2023",
        badge: "badge-j",
        github: "",
        pdf: "",
        otherinfo: "",
        tag: "Visual Analytics",
        about: "This article outlines a research and development agenda for making VA application research more rigorous and impactful",
        id: "CGA23Viewpoint",
    },
    {
        imgurl: "tvcg23dminer.png",
        title: "DMiner: Dashboard Design Mining and Recommendation",
        author: "Yanna Lin, Haotian Li, Aoyu Wu, Yong Wang, and Huamin Qu",
        bookShort: "TVCG",
        book: "IEEE Transactions on Visualization and Computer Graphics",
        type: "Journal",
        year: "2023 (To appear)",
        badge: "badge-j",
        github: "",
        pdf: "https://arxiv.org/pdf/2209.01599.pdf",
        otherinfo: "",
        tag: "AI4VIS",
        about: "We propose a novel approach to mine design patterns in Tableau dashboards and develop recommenders for view coordination and interaction.",
        id: "TVCG23",
    },
    {
        imgurl: "chi23datavideo.png",
        title: "Is It the End? Guidelines for Cinematic Endings in Data Videos",
        author: "Xian Xu, Aoyu Wu, Leni Yang, Rong Huang, Zheng Wei, David Yip, and Huamin Qu",
        bookShort: "CHI",
        book: "ACM Conference on Human Factors in Computing Systems",
        type: "Conference",
        year: "2023",
        badge: "badge-c",
        github: "",
        pdf: "",
        otherinfo: "",
        tag: "Smart Campus / Vis for Masses",
        about: "Working the film experts, we explore how to design cinematic endings for data videos.",
        id: "CHI23",
    },
    {
        imgurl: "pvis2023vrvideo.png",
        title: "Understanding 3D Data Videos: From Screens to Virtual Reality",
        author: "Leni Yang, Aoyu Wu, Wai Tong, Xian Xu, Zheng Wei, and Huamin Qu",
        bookShort: "PacificVis",
        book: "Proc. of the IEEE Pacific Visualization Symposium",
        type: "Conference",
        year: "2023",
        badge: "badge-o",
        github: "",
        pdf: "",
        otherinfo: "",
        tag: "Smart Campus / Vis for Masses",
        about: "We explore the idea of VR data videos by learning from and adapting 3D data videos on screens.",
        id: "PVIS23",
    },
    {
        imgurl: "work2022revast.png",
        title: "In Defence of Visual Analytics Systems: Replies to Critics",
        author: "Aoyu Wu, Dazhen Deng, Furui Cheng, Yingcai Wu, Shixia Liu, and Huamin Qu",
        bookShort: "VIS/TVCG",
        book: "IEEE Transactions on Visualization and Computer Graphics (Proc. of VIS)",
        type: "Journal",
        year: "2022",
        badge: "badge-o",
        github: "https://re-vast.github.io/",
        pdf: "https://arxiv.org/pdf/2201.09772.pdf",
        otherinfo: "",
        tag: "Visual Analytics",
        about: "We discuss 36 common criticisms of VA systems and responses to those criticisms.",
        id: "VIS22a",
    },
    {
        imgurl: "work2022dashbot.png",
        title: "DashBot: Insight-Driven Dashboard Generation Based on Deep Reinforcement Learning",
        author: "Dazhen Deng, Aoyu Wu, Huamin Qu, and Yingcai Wu",
        bookShort: "VIS/TVCG",
        book: "IEEE Transactions on Visualization and Computer Graphics (Proc. of VIS)",
        type: "Journal",
        year: "2022",
        badge: "badge-o",
        // github: "https://re-vast.github.io/",
        // pdf: "https://arxiv.org/pdf/2201.09772.pdf",
        otherinfo: "",
        tag: "AI4VIS",
        about: "We propose using deep reinforcement learning to generate analytical dashboards that can use well-established visualization knowledge and the estimation capacity of reinforcement learning.",
        id: "VIS22b",
    },
    {
        imgurl: "work2022-tvcg-gesture.png",
        title: "GestureLens: Visual Analysis of Gestures in Presentation Videos",
        author: "Haipeng Zeng, Xingbo Wang, Yong Wang, Aoyu Wu, Ting Chuen Pong, and Huamin Qu",
        bookShort: "TVCG",
        book: "IEEE Transactions on Visualization and Computer Graphics",
        type: "Journal",
        year: "2022",
        badge: "badge-j",
        github: "",
        pdf: "https://arxiv.org/pdf/2204.08894.pdf",
        otherinfo: "",
        tag: "Multimodal Analysis and Visualization",
        about: "We contribute a visual analytic approach that assists professional public speaking coaches in improving their practice of gesture training through analyzing presentation videos.",
        id: "TVCG22G",
    },
    {
        imgurl: "work2022-euro-mis.png",
        title: "Misinformed by Visualization: What Do We Learn From Misinformative Visualizations?",
        author: "Leo Yu-Ho Lo, Ayush Gupta, Kento Shigyo, Aoyu Wu, Enrico Bertini, and Huamin Qu",
        bookShort: "CGF/EuroVis",
        book: "Computer Graphics Forum",
        type: "Journal",
        year: "2022",
        badge: "badge-j",
        github: "",
        pdf: require(`@/assets/pdf/2022EuroVis_misinformative.pdf`).default,
        otherinfo: "",
        tag: "Smart Campus / Vis for Masses",
        about: "We present a comprehensive design space (74 types) of misinformative visualization and discuss opportunities for intervention and treatment.",
        id: "CGF22",
    },
    {
        imgurl: "work2022-chi-math.gif",
        title: "ComputableViz: Mathematical Operators as a Formalism for Visualization Processing and Analysis",
        author: "Aoyu Wu, Wai Tong, Haotian Li, Dominik Moritz, Yong Wang, and Huamin Qu",
        bookShort: "CHI",
        book: "ACM Conference on Human Factors in Computing Systems",
        type: "Conference",
        year: "2022",
        badge: "badge-c",
        github: "",
        pdf: "https://arxiv.org/pdf/2204.00856.pdf",
        otherinfo: "",
        tag: "AI4VIS",
        about: "Let us perform math computations on visualizations! We discuss many interesting applications, such as composting them on AR and automatically making charts assessable.",
        id: "CHI22c",
    },
    {
        imgurl: "work2022-chi-retrieval.png",
        title: "Structure-aware Visualization Retrieval",
        author: "Haotian Li, Yong Wang, Aoyu Wu, Huan Wei, and Huamin Qu",
        bookShort: "CHI",
        book: "ACM Conference on Human Factors in Computing Systems",
        type: "Conference",
        year: "2022",
        badge: "badge-c",
        github: "",
        pdf: "https://arxiv.org/pdf/2202.05960.pdf",
        otherinfo: "",
        tag: "AI4VIS",
        award: "Honorable Mention",
        about: "Searching SVG-based visualizations by both visual and structural information.",
        id: "CHI22s",
    },
    {
        imgurl: "work2021Intro.png",
        title: "Explaining with Examples: Lessons Learned from Crowdsourced Introductory Description of Information Visualizations",
        author: "Leni Yang,  Cindy Xiong,  Jason K. Wong,  Aoyu Wu  and Huamin Qu",
        bookShort: "TVCG",
        book: "IEEE Transactions on Visualization and Computer Graphics",
        type: "Journal",
        year: "2021",
        badge: "badge-j",
        otherinfo: "",
        pdf: "https://arxiv.org/pdf/2112.12364.pdf",
        // pdf: require(`@/assets/pdf/2021Intro.pdf`).default,
        tag: "Smart Campus / Vis for Masses",
        doi: "10.1109/TVCG.2021.3128157",
        about: "Having difficulties in explaining uncommon visualizations to the public? Tell an example! And more suggestions in the paper.",
        id: "TVCG21e",
    },
    {
        imgurl: "work2021multivision.gif",
        title: "MultiVision: Designing Analytical Dashboards with Deep Learning Based Recommendation",
        author: "Aoyu Wu, Yun Wang, Mengyu Zhou, Xinyi He, Haidong Zhang, Huamin Qu and Dongmei Zhang",
        bookShort: "VIS/TVCG",
        book: "IEEE Transactions on Visualization and Computer Graphics (Proc. of VIS)",
        type: "Journal",
        year: "2021",
        badge: "badge-j",
        otherinfo: "",
        pdf: "https://arxiv.org/abs/2107.07823.pdf",
        github: "https://github.com/Franches/MultiVision",
        tag: "AI4VIS",
        doi: "10.1109/TVCG.2021.3114826",
        about: "We present the first DL model for recommending dashboards given a tabular dataset. The model was trained on a large corpus containing 20k Excel sheets.",
        id: "VIS21",
    },
    {
        imgurl: "work2021Survey.png",
        title: "AI4VIS: Survey on Artificial Intelligence Approaches for Data Visualization",
        author: "Aoyu Wu, Yun Wang, Xinhuan Shu, Dominik Moritz, Weiwei Cui, Haidong Zhang, Dongmei Zhang and Huamin Qu  ",
        bookShort: "TVCG",
        book: "IEEE Transactions on Visualization and Computer Graphics",
        type: "Journal",
        year: "2021",
        badge: "badge-j",
        otherinfo: "",
        pdf: "https://arxiv.org/pdf/2102.01330.pdf",
        github: "https://ai4vis.github.io/",
        slides: "https://drive.google.com/file/d/1_Ihmdf9_M9Ee5RAmYoLF9Whu1DLUeFXp/view?usp=sharing",
        tag: "AI4VIS",
        doi: "10.1109/TVCG.2021.3099002",
        about: "Visualizations themselves have become a data format. Akin to other data formats such as text and images, visualizations are increasingly created, stored, shared, and (re-)used with artificial intelligence (AI) techniques.",
        id: "TVCG21",
    },
    {
        imgurl: "work2021LQ2.png",
        title: "Learning to Automate Chart Layout Configurations Using Crowdsourced Paired Comparison",
        author: "Aoyu Wu, Liwenhan Xie, Bongshin Lee, Yun Wang, Weiwei Cui and Huamin Qu",
        bookShort: "CHI",
        book: "ACM Conference on Human Factors in Computing Systems",
        type: "Conference",
        year: "2021",
        badge: "badge-c",
        otherinfo: "",
        pdf: "https://arxiv.org/pdf/2101.03680.pdf",
        github: "https://github.com/shellywhen/LQ2",
        tag: "AI4VIS",
        doi: "10.1145/3411764.3445179",
        about: "We propose a method for automating chart configurations by learning from human preference. The model achieved at least human-level performance while saving users 70 seconds on average.",
        id: "CHI21",
    },
    {
        imgurl: "work2020mvf.png",
        title: "MobileVisFixer: Tailoring Web Visualizations for Mobile Phones Leveraging an Explainable Reinforcement Learning Framework",
        author: "Aoyu Wu, Wai Tong, Tim Dwyer, Bongshin Lee, Petra Isenberg, and Huamin Qu",
        bookShort: "VIS/TVCG",
        book: "IEEE Transactions on Visualization and Computer Graphics (InfoVis)",
        type: "Journal",
        badge: "badge-j",
        year: "2020",
        otherinfo: "",
        pdf: "https://arxiv.org/pdf/2008.06678.pdf",
        preview: "https://www.youtube.com/watch?v=7vJkxLGdRBU&feature=youtu.be",
        tag: "AI4VIS",
        doi: "10.1109/TVCG.2020.3030435",
        about: "More than half of web visualizations are not mobile-friendly. We contribute an automated method to help.",
        id: "VIS20M",
    },
    {
        imgurl: "work2020gif.gif",
        title: "What Makes a Data-GIF Understandable?",
        author: "Xinhuan Shu, Aoyu Wu, Junxiu Tang, Benjamin Bach, Yingcai Wu, and Huamin Qu",
        bookShort: "VIS/TVCG",
        book: "IEEE Transactions on Visualization and Computer Graphics (InfoVis)",
        type: "Journal",
        year: "2020",
        badge: "badge-j",
        otherinfo: "",
        pdf: "https://arxiv.org/pdf/2008.07227.pdf",
        demo: "https://data-gifs.github.io/Data-GIFs/",
        tag: "Smart Campus / Vis for Masses",
        doi: "10.1109/TVCG.2020.3030396",
        about: "Data-Gifs are becoming an emerging genre for data storytelling. We study why people might have difficulties understanding them and provide suggestions for designers.",
        id: "VIS20W",
    },
    {
        imgurl: "work2020CHIVoiceCoach.png",
        title: "VoiceCoach: Interactive Evidence-based Training for Voice Modulation Skills in Public Speaking",
        author: "Xingbo Wang, Haipeng Zeng, Yong Wang, Aoyu Wu, Zhida Sun, Xiaojuan Ma and Huamin Qu",
        bookShort: "CHI",
        book: "ACM Conference on Human Factors in Computing Systems",
        type: "Conference",
        year: "2020",
        badge: "badge-c",
        otherinfo: "",
        pdf: "https://arxiv.org/abs/2001.07876",
        tag: "Multimodal Analysis and Visualization",
        doi: "10.1145/3313831.3376726",
        id: "CHI20",
    },
    {
        imgurl: "work2019EmoCo.png",
        title: "EmoCo: Visual Analysis of Emotion Coherence in Presentation Videos",
        author: "Haipeng Zeng, Xingbo Wang, Aoyu Wu, Yong Wang, Quan Li, Alex Endert and Huamin Qu",
        book: "IEEE Transactions on Visualization and Computer Graphics (VAST)",
        year: "2019",
        bookShort: "VIS/TVCG",
        type: "Journal",
        badge: "badge-j",
        otherinfo: "",
        pdf: "https://arxiv.org/abs/1907.12918",
        tag: "Multimodal Analysis and Visualization",
        doi: "10.1109/TVCG.2019.2934656",
        id: "VIS19",
    },
    {
        imgurl: "work2019SLens.png",
        title: "SpeechLens: Using Visual Analytics to Explore Narration Strategies in TED Talks",
        author: "Linping Yuan, Yuanzhe Chen, Siwei Fu, Aoyu Wu and Huamin Qu",
        bookShort: "BigComp",
        book: "IEEE International Conference on Big Data and Smart Computing",
        type: "Conference",
        badge: "badge-c",
        year: "2019",
        otherinfo: "",
        pdf: "",
        tag: "Multimodal Analysis and Visualization",
    },
    {
        imgurl: "work2018TED.png",
        title: "Multimodal Analysis of Video Collections: Visual Exploration of Presentation Techniques in TED Talks",
        author: "Aoyu Wu and Huamin Qu",
        bookShort: "TVCG",
        book: "IEEE Transactions on Visualization and Computer Graphics",
        type: "Journal",
        badge: "badge-j",
        year: "2018",
        pdf: require(`@/assets/pdf/2018VisTED.pdf`).default,
        otherinfo: " Presented on IEEE VIS 2019.",
        tag: "Multimodal Analysis and Visualization",
        doi: "10.1109/TVCG.2018.2889081",
        id: "TVCG18",
    },
    {
        imgurl: "work2018Pulse.png",
        title: "Pulse: Toward a Smart Campus by Communicating Real-time Wi-Fi Access Data",
        author: "Aoyu Wu, Bon Kyung Ku, Furui Cheng, Xinhuan Shu, Abishek Puri, Yifang Wang and Huamin Qu",
        bookShort: "VisComm",
        book: "Workshop on Visualization for Communication, the IEEE Visualization Conference (VIS'18)",
        type: "Workshop",
        badge: "badge-o",
        year: "2018",
        otherinfo: "",
        pdf: "https://arxiv.org/abs/1810.00161",
        tag: "Smart Campus / Vis for Masses",
        id: "VisComm18",
    },
] as any[];

export default defineComponent({
    name: "App",
    components: { SinglePaper, CommentOutlined, PaperLink },
    setup() {
        for (let p of publications) {
            p.img = require(`@/assets/images/${p.imgurl}`);
        }

        const tags = [
            "All",
            ..._.chain(publications)
                .groupBy((x) => x.tag)
                .map((v, key) => key)
                .value(),
        ];
        console.log(tags);

        const state = reactive({
            yearFilter: [],
            currentTab: "All",
        });

        var filteredPub = computed(() => {
            console.log(state.currentTab, state.currentTab == "All");

            if (state.currentTab == "All") return publications;
            else return publications.filter((p) => p.tag === state.currentTab);
        });

        return {
            publications,
            ...toRefs(state),
            tags,
            filteredPub,
        };
    },
    computed: {},
});
</script>

<style rel="stylesheet" lang="scss">
$blue: #263459;
$color1: #96351e;
$color2: #dbb98f;

#section-research {
    .ant-card-head {
        min-height: 30px;
        .ant-card-head-title {
            padding-top: 8px;
            padding-bottom: 8px;
        }
    }

    img {
        border: 1px solid #5e7595;
    }

    .ant-card-body {
        padding: 8px 24px;
    }

    .anticon {
        vertical-align: 0.1em;
    }

    .ant-tabs {
        font-size: 15px;

        .research-head {
            font-size: 1.2em;
            font-weight: 500;
            // text-decoration: underline;
            // text-decoration-color: $color1;
            // text-decoration-thickness: 2px;
        }

        span.work-short {
            // border-bottom: 2px solid $color1;

            // text-decoration: underline;
            // text-decoration-color: $color1;
            // text-decoration-thickness: 2px;
        }
    }
}
</style>
